import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { CertificationSpend } from '@/hooks/useDiversityAnalytics';
import { CertificationDisplayBadge } from './CertificationMultiSelect';

interface CertificationSpendChartProps {
  data: CertificationSpend[] | undefined;
  isLoading: boolean;
}

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

function formatPercentage(value: number): string {
  return `${value.toFixed(1)}%`;
}

// Color palette for bars
const BAR_COLORS = [
  'hsl(var(--primary))',
  'hsl(210, 80%, 55%)',
  'hsl(270, 60%, 60%)',
  'hsl(330, 70%, 55%)',
  'hsl(150, 60%, 45%)',
  'hsl(30, 80%, 55%)',
  'hsl(180, 60%, 45%)',
  'hsl(60, 70%, 50%)',
  'hsl(240, 60%, 60%)',
  'hsl(0, 70%, 55%)',
  'hsl(120, 50%, 45%)',
  'hsl(300, 60%, 55%)',
  'hsl(45, 80%, 50%)',
];

export function CertificationSpendChart({ data, isLoading }: CertificationSpendChartProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-64" />
          <Skeleton className="h-4 w-96" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Spend by Certification Type</CardTitle>
          <CardDescription>
            Distribution of diversity spend across certification categories
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex h-[300px] items-center justify-center text-muted-foreground">
            No diversity spend data available for the selected period
          </div>
        </CardContent>
      </Card>
    );
  }

  // Prepare chart data
  const chartData = data.map((item, index) => ({
    name: item.certificationName,
    spend: item.spendCents / 100,
    spendCents: item.spendCents,
    jobs: item.jobCount,
    vendors: item.vendorCount,
    percentDiverse: item.percentageOfDiverseSpend,
    percentTotal: item.percentageOfTotalSpend,
    fill: BAR_COLORS[index % BAR_COLORS.length],
  }));

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="rounded-lg border bg-background p-3 shadow-lg">
          <p className="font-medium">{data.name}</p>
          <div className="mt-2 space-y-1 text-sm">
            <p>Spend: <span className="font-medium">{formatCurrency(data.spendCents)}</span></p>
            <p>Jobs: <span className="font-medium">{data.jobs}</span></p>
            <p>Vendors: <span className="font-medium">{data.vendors}</span></p>
            <p>% of Diversity Spend: <span className="font-medium">{formatPercentage(data.percentDiverse)}</span></p>
            <p>% of Total Spend: <span className="font-medium">{formatPercentage(data.percentTotal)}</span></p>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Spend by Certification Type</CardTitle>
        <CardDescription>
          Distribution of diversity spend across certification categories
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Vendors with multiple certifications are counted in each applicable category. 
            The sum of certification spend may exceed total diversity spend.
          </AlertDescription>
        </Alert>

        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
              <XAxis
                type="number"
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              />
              <YAxis
                type="category"
                dataKey="name"
                width={150}
                tick={{ fontSize: 12 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="spend" radius={[0, 4, 4, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Certification Type</TableHead>
                <TableHead className="text-right">Spend Amount</TableHead>
                <TableHead className="text-right">% of Diversity</TableHead>
                <TableHead className="text-right">% of Total</TableHead>
                <TableHead className="text-right">Jobs</TableHead>
                <TableHead className="text-right">Vendors</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((item) => (
                <TableRow key={item.certificationTypeId}>
                  <TableCell>
                    <CertificationDisplayBadge name={item.certificationName} />
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(item.spendCents)}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatPercentage(item.percentageOfDiverseSpend)}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatPercentage(item.percentageOfTotalSpend)}
                  </TableCell>
                  <TableCell className="text-right">{item.jobCount}</TableCell>
                  <TableCell className="text-right">{item.vendorCount}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
